import { SidebarTrigger } from '../../components/ui/sidebar';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useSemanticGaps,
  useResolveSemanticGap,
  useDismissSemanticGap,
  useApproveSemanticGap,
  useDeleteSemanticGap,
} from '../../hooks/useSemanticGaps';
import type { SemanticGap, SemanticGapStatus, SemanticGapType, SemanticGapPriority } from '../../services/semanticGap.service';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Loader2,
  Search,
  MoreHorizontal,
  CheckCircle2,
  XCircle,
  ThumbsUp,
  Trash2,
  TrendingUp,
  AlertTriangle,
  Download,
} from 'lucide-react';

const GAP_TYPE_LABELS: Record<SemanticGapType, string> = {
  missing_metric: 'Missing Metric',
  missing_dimension: 'Missing Dimension',
  missing_relationship: 'Missing Relationship',
  missing_source: 'Missing Source',
  data_quality: 'Data Quality',
  ambiguous_naming: 'Ambiguous Naming',
};

const STATUS_LABELS: Record<SemanticGapStatus, string> = {
  detected: 'Detected',
  approved: 'Approved',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

const PRIORITY_LABELS: Record<SemanticGapPriority, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

function csvEscape(value: string | number | null | undefined): string {
  const str = value === null || value === undefined ? '' : String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function gapsToCsv(gaps: SemanticGap[]): string {
  const header = [
    'Title',
    'Type',
    'Priority',
    'Hits',
    'Status',
    'Description',
    'Suggested Resolution',
    'Resolved At',
    'Last Updated',
    'ID',
  ];
  const rows = gaps.map((gap) => [
    gap.title,
    GAP_TYPE_LABELS[gap.gap_type] || gap.gap_type,
    PRIORITY_LABELS[gap.priority] || gap.priority,
    gap.hit_count,
    STATUS_LABELS[gap.status] || gap.status,
    gap.description,
    gap.suggested_resolution || '',
    gap.resolved_at || '',
    gap.last_updated || '',
    gap.id,
  ]);
  return [header, ...rows]
    .map((row) => row.map(csvEscape).join(','))
    .join('\n');
}

function downloadGapsCsv(gaps: SemanticGap[]) {
  const csv = gapsToCsv(gaps);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `semantic-gaps-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function StatusBadge({ status }: { status: SemanticGapStatus }) {
  const variant = {
    detected: 'warning' as const,
    approved: 'info' as const,
    in_progress: 'default' as const,
    resolved: 'success' as const,
    dismissed: 'secondary' as const,
  }[status];

  return <Badge variant={variant}>{STATUS_LABELS[status]}</Badge>;
}

function PriorityBadge({ priority }: { priority: SemanticGapPriority }) {
  const variant = {
    high: 'destructive' as const,
    medium: 'warning' as const,
    low: 'secondary' as const,
  }[priority];

  return <Badge variant={variant}>{PRIORITY_LABELS[priority]}</Badge>;
}

export default function SemanticGaps() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [selectedGap, setSelectedGap] = useState<SemanticGap | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'resolve' | 'dismiss' | 'delete';
    gap: SemanticGap;
  } | null>(null);

  const { data, isLoading, error } = useSemanticGaps();
  const resolveMutation = useResolveSemanticGap();
  const dismissMutation = useDismissSemanticGap();
  const approveMutation = useApproveSemanticGap();
  const deleteMutation = useDeleteSemanticGap();

  const gaps = useMemo(() => {
    const allGaps = data?.records || [];

    let filtered = allGaps;

    if (statusFilter === 'open') {
      filtered = filtered.filter(
        (g) => !['resolved', 'dismissed'].includes(g.status)
      );
    } else if (statusFilter !== 'all') {
      filtered = filtered.filter((g) => g.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (g) =>
          g.title.toLowerCase().includes(query) ||
          g.description.toLowerCase().includes(query) ||
          (g.suggested_resolution || '').toLowerCase().includes(query)
      );
    }

    return filtered.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      const pa = priorityOrder[a.priority] ?? 1;
      const pb = priorityOrder[b.priority] ?? 1;
      if (pa !== pb) return pa - pb;
      return b.hit_count - a.hit_count;
    });
  }, [data?.records, searchQuery, statusFilter]);

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, gap } = confirmAction;

    try {
      if (type === 'resolve') {
        await resolveMutation.mutateAsync(gap.id);
      } else if (type === 'dismiss') {
        await dismissMutation.mutateAsync(gap.id);
      } else if (type === 'delete') {
        await deleteMutation.mutateAsync(gap.id);
      }
    } finally {
      setConfirmAction(null);
    }
  };

  const isPending =
    resolveMutation.isPending ||
    dismissMutation.isPending ||
    approveMutation.isPending ||
    deleteMutation.isPending;

  return (
    <div
      className="w-full flex flex-col bg-background overflow-hidden"
      style={{ height: 'calc(100vh)' }}
    >
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <SidebarTrigger className="md:hidden h-5 w-5" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/develop">Develop</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Semantic Gaps</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <h1 className="text-xl font-semibold text-foreground">
            Semantic Gaps
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="detected">Detected</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search gaps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 pl-9"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => downloadGapsCsv(gaps)}
            disabled={isLoading || gaps.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Download CSV
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Loading gaps...
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">
              Failed to load semantic gaps. Please try again.
            </p>
          </div>
        ) : gaps.length === 0 ? (
          <div className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {statusFilter === 'open'
                ? 'No open gaps. Ellie will flag gaps here when users ask questions the semantic layer can\'t answer.'
                : 'No gaps match the current filter.'}
            </p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[30%]">Title</TableHead>
                  <TableHead className="w-[15%]">Type</TableHead>
                  <TableHead className="w-[10%]">Priority</TableHead>
                  <TableHead className="w-[10%] text-center">Hits</TableHead>
                  <TableHead className="w-[10%]">Status</TableHead>
                  <TableHead className="w-[15%]">Suggested Fix</TableHead>
                  <TableHead className="w-[10%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gaps.map((gap) => (
                  <TableRow
                    key={gap.id}
                    className="cursor-pointer"
                    onClick={() => {
                      setSelectedGap(gap);
                      setDetailOpen(true);
                    }}
                  >
                    <TableCell className="font-medium">
                      <div className="truncate" title={gap.title}>
                        {gap.title}
                      </div>
                      <div className="text-xs text-muted-foreground truncate mt-0.5">
                        {gap.description.length > 80
                          ? gap.description.slice(0, 80) + '...'
                          : gap.description}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {GAP_TYPE_LABELS[gap.gap_type] || gap.gap_type}
                      </span>
                    </TableCell>
                    <TableCell>
                      <PriorityBadge priority={gap.priority} />
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <TrendingUp className="h-3 w-3 text-muted-foreground" />
                        <span className="font-mono text-sm">
                          {gap.hit_count}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={gap.status} />
                    </TableCell>
                    <TableCell>
                      <div
                        className="text-xs text-muted-foreground truncate"
                        title={gap.suggested_resolution || ''}
                      >
                        {gap.suggested_resolution || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {gap.status === 'detected' && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                approveMutation.mutate(gap.id);
                              }}
                            >
                              <ThumbsUp className="h-4 w-4 mr-2" />
                              Approve
                            </DropdownMenuItem>
                          )}
                          {!['resolved', 'dismissed'].includes(gap.status) && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmAction({
                                  type: 'resolve',
                                  gap,
                                });
                              }}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Mark Resolved
                            </DropdownMenuItem>
                          )}
                          {!['resolved', 'dismissed'].includes(gap.status) && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmAction({
                                  type: 'dismiss',
                                  gap,
                                });
                              }}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Dismiss
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmAction({ type: 'delete', gap });
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedGap?.title}</DialogTitle>
            <DialogDescription>
              {selectedGap && GAP_TYPE_LABELS[selectedGap.gap_type]}
            </DialogDescription>
          </DialogHeader>
          {selectedGap && (
            <DialogBody>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <StatusBadge status={selectedGap.status} />
                  <PriorityBadge priority={selectedGap.priority} />
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <TrendingUp className="h-3 w-3" />
                    {selectedGap.hit_count} hit
                    {selectedGap.hit_count !== 1 ? 's' : ''}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium mb-1">Description</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {selectedGap.description}
                  </p>
                </div>

                {selectedGap.suggested_resolution && (
                  <div>
                    <h4 className="text-sm font-medium mb-1">
                      Suggested Resolution
                    </h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {selectedGap.suggested_resolution}
                    </p>
                  </div>
                )}

                {selectedGap.source_thread_ids.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-1">
                      Related Conversations
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {selectedGap.source_thread_ids.length} conversation
                      {selectedGap.source_thread_ids.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}
              </div>
            </DialogBody>
          )}
          <DialogFooter>
            {selectedGap &&
              !['resolved', 'dismissed'].includes(selectedGap.status) && (
                <div className="flex gap-2">
                  {selectedGap.status === 'detected' && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        approveMutation.mutate(selectedGap.id);
                        setDetailOpen(false);
                      }}
                      disabled={isPending}
                    >
                      <ThumbsUp className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDetailOpen(false);
                      setConfirmAction({
                        type: 'dismiss',
                        gap: selectedGap,
                      });
                    }}
                    disabled={isPending}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Dismiss
                  </Button>
                  <Button
                    onClick={() => {
                      setDetailOpen(false);
                      setConfirmAction({
                        type: 'resolve',
                        gap: selectedGap,
                      });
                    }}
                    disabled={isPending}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark Resolved
                  </Button>
                </div>
              )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog
        open={!!confirmAction}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.type === 'resolve' && 'Mark as Resolved'}
              {confirmAction?.type === 'dismiss' && 'Dismiss Gap'}
              {confirmAction?.type === 'delete' && 'Delete Gap'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.type === 'resolve' &&
                `Mark "${confirmAction.gap.title}" as resolved? This means the underlying issue has been fixed.`}
              {confirmAction?.type === 'dismiss' &&
                `Dismiss "${confirmAction.gap.title}"? This hides it from the open list.`}
              {confirmAction?.type === 'delete' &&
                `Permanently delete "${confirmAction.gap.title}"? This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmAction(null)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              variant={
                confirmAction?.type === 'delete' ? 'destructive' : 'default'
              }
              onClick={handleConfirmAction}
              disabled={isPending}
            >
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {confirmAction?.type === 'resolve' && 'Resolve'}
              {confirmAction?.type === 'dismiss' && 'Dismiss'}
              {confirmAction?.type === 'delete' && 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
